import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './components/Header';
import Footer from './components/Footer';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3481';

function App() {
  const [stars, setStars] = useState(1000);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [price, setPrice] = useState({ rub: 0, usdt: 0 });
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [usdtWallet, setUsdtWallet] = useState(null);
  const [sbpRedirect, setSbpRedirect] = useState(null);
  const checkTimeoutRef = React.useRef(null);

  useEffect(() => {
    calculatePrice();
  }, [stars]);

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/calculate`, { stars });
      setPrice({
        rub: response.data.rubPrice,
        usdt: response.data.usdtPrice
      });
    } catch (error) {
      console.error('Ошибка расчета:', error);
    }
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const normalizeUsername = (input) => {
    if (!input || !input.trim()) return '';
    
    let normalized = input.trim();
    
    // Убираем https://t.me/ или http://t.me/
    normalized = normalized.replace(/^https?:\/\/(www\.)?t\.me\//, '');
    
    // Убираем @ в начале
    normalized = normalized.replace(/^@/, '');
    
    // Убираем пробелы
    normalized = normalized.replace(/\s+/g, '');
    
    return normalized;
  };

  const checkUsernameExists = async (normalizedUsername) => {
    try {
      const backendResponse = await axios.get(`${API_URL}/api/check-username/${normalizedUsername}`, {
        timeout: 8000
      });
      
      if (backendResponse.data.exists && backendResponse.data.avatar) {
        setUserInfo({
          avatar: backendResponse.data.avatar,
          name: backendResponse.data.name,
          description: backendResponse.data.description,
          username: normalizedUsername
        });
      } else {
        setUserInfo(null);
      }
      
      return backendResponse.data.exists;
    } catch (error) {
      console.error('Ошибка проверки username:', error);
      setUserInfo(null);
      // Если проверка не удалась, разрешаем продолжать (на случай проблем с API)
      return true;
    }
  };

  const validateUsername = async (input) => {
    const normalized = normalizeUsername(input);
    
    if (!normalized) {
      return { valid: false, error: 'Укажите username получателя' };
    }
    
    // Telegram username: 5-32 символа, только буквы, цифры и подчеркивания
    if (normalized.length < 5 || normalized.length > 32) {
      return { valid: false, error: 'Username должен быть от 5 до 32 символов' };
    }
    
    if (!/^[a-zA-Z0-9_]+$/.test(normalized)) {
      return { valid: false, error: 'Username может содержать только буквы, цифры и подчеркивания' };
    }
    
    return { valid: true, normalized, error: '' };
  };

  const handleStarsChange = (e) => {
    const value = parseInt(e.target.value.replace(/\s/g, '')) || 0;
    if (value >= 10 && value <= 100000) {
      setStars(value);
    }
  };

  const handleUsernameChange = async (e) => {
    const value = e.target.value;
    setUsername(value);
    setUsernameError('');
    setCheckingUsername(false);
    setUserInfo(null); // Сбрасываем информацию при изменении
    
    // Очищаем предыдущий таймер
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    if (value) {
      const validation = await validateUsername(value);
      if (!validation.valid) {
        setUsernameError(validation.error);
        return;
      }
      
      // Проверка существования username в Telegram (с задержкой для дебаунса)
      checkTimeoutRef.current = setTimeout(async () => {
        setCheckingUsername(true);
        try {
          const exists = await checkUsernameExists(validation.normalized);
          if (!exists) {
            setUsernameError('Пользователь с таким username не найден в Telegram');
            setUserInfo(null);
          }
        } catch (error) {
          console.error('Ошибка проверки:', error);
          setUserInfo(null);
          // Не блокируем если проверка не удалась
        } finally {
          setCheckingUsername(false);
        }
      }, 800); // Дебаунс 800ms
    }
  };

  const handlePaymentMethod = async (method) => {
    // Валидация username
    const validation = await validateUsername(username);
    if (!validation.valid) {
      setUsernameError(validation.error);
      return;
    }
    
    // Дополнительная проверка существования перед оплатой
    setCheckingUsername(true);
    try {
      const exists = await checkUsernameExists(validation.normalized);
      if (!exists) {
        setUsernameError('Пользователь с таким username не найден в Telegram');
        setCheckingUsername(false);
        return;
      }
    } catch (error) {
      console.error('Ошибка проверки:', error);
      // Продолжаем если проверка не удалась
    } finally {
      setCheckingUsername(false);
    }

    setLoading(true);
    setPaymentMethod(method);

    try {
      const normalizedUsername = validation.normalized;
      
      if (method === 'usdt') {
        const response = await axios.post(`${API_URL}/api/payment/usdt`, {
          stars,
          username: normalizedUsername,
          userId: 'user_' + Date.now()
        });
        setUsdtWallet(response.data);
        setLoading(false); // Убираем loading после успешного получения данных
      } else if (method === 'sbp') {
        const response = await axios.post(`${API_URL}/api/payment/sbp`, {
          stars,
          username: normalizedUsername,
          returnUrl: window.location.origin + '/success',
          failedUrl: window.location.origin + '/failed'
        });
        setSbpRedirect(response.data.redirectUrl);
        // Для СБП сразу редиректим, loading не нужен
        window.location.href = response.data.redirectUrl;
      }
    } catch (error) {
      console.error('Ошибка создания платежа:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка создания платежа. Попробуйте позже.';
      alert(errorMessage);
      setLoading(false);
      setPaymentMethod(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Скопировано в буфер обмена!');
  };

  return (
    <div className="App">
      <Header />
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h1>Telegram Stars</h1>
            <p className="subtitle">Купите звезды для вашего бота</p>
          </div>

          <div className="username-input-section">
            <label htmlFor="username">
              Username получателя
              <span className="label-hint">(@username, t.me/username или просто username)</span>
            </label>
            <div className="username-input-wrapper">
              <input
                id="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                className={`username-input ${usernameError ? 'error' : ''} ${checkingUsername ? 'checking' : ''}`}
                placeholder="@username или https://t.me/username"
                disabled={checkingUsername}
              />
              {checkingUsername && (
                <div className="checking-indicator">
                  <div className="checking-spinner"></div>
                </div>
              )}
            </div>
            {usernameError && <div className="error-message">{usernameError}</div>}
            {userInfo && !usernameError && (
              <div className="user-info-card">
                <div className="user-avatar">
                  <img src={userInfo.avatar} alt={userInfo.name || userInfo.username} />
                </div>
                <div className="user-details">
                  <div className="user-name">{userInfo.name || `@${userInfo.username}`}</div>
                  <div className="user-username">@{userInfo.username}</div>
                  {userInfo.description && (
                    <div className="user-description">{userInfo.description}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="stars-input-section">
            <label htmlFor="stars">Количество звезд</label>
            <div className="input-group">
              <input
                id="stars"
                type="text"
                min="10"
                max="100000"
                value={formatNumber(stars)}
                onChange={handleStarsChange}
                className="stars-input"
                placeholder="Введите количество"
              />
              <div className="quick-buttons">
                <button onClick={() => setStars(500)}>500</button>
                <button onClick={() => setStars(1000)}>1 000</button>
                <button onClick={() => setStars(5000)}>5 000</button>
                <button onClick={() => setStars(25000)}>25 000</button>
              </div>
            </div>
          </div>

          <div className="price-section">
            <div className="price-item">
              <span className="price-label">Стоимость:</span>
              <span className="price-value">{formatNumber(price.rub.toFixed(2))} ₽</span>
            </div>
            <div className="price-item">
              <span className="price-label">USDT:</span>
              <span className="price-value">{price.usdt.toFixed(6)} USDT</span>
            </div>
          </div>

          {!paymentMethod && (
            <div className="payment-methods">
              <h3>Выберите способ оплаты</h3>
              <div className="payment-buttons">
                <button
                  className="payment-btn sbp-btn"
                  onClick={() => handlePaymentMethod('sbp')}
                  disabled={loading || !username || !!usernameError}
                >
                  <span>💳</span>
                  <span>СБП</span>
                </button>
                <button
                  className="payment-btn usdt-btn"
                  onClick={() => handlePaymentMethod('usdt')}
                  disabled={loading || !username || !!usernameError}
                >
                  <span>₮</span>
                  <span>USDT TRC20</span>
                </button>
              </div>
              {(!username || usernameError) && (
                <p className="payment-hint">Укажите username получателя для продолжения</p>
              )}
            </div>
          )}

          {paymentMethod === 'usdt' && usdtWallet && !loading && (
            <div className="usdt-payment">
              <h3>Оплата USDT TRC20</h3>
              <div className="wallet-info">
                <div className="recipient-info">
                  <p className="wallet-label">Получатель:</p>
                  <p className="recipient-username">@{usdtWallet.username}</p>
                </div>
                <p className="wallet-label">Адрес кошелька:</p>
                <div className="wallet-address" onClick={() => copyToClipboard(usdtWallet.wallet)}>
                  <code>{usdtWallet.wallet}</code>
                  <span className="copy-icon">📋</span>
                </div>
                <p className="wallet-amount">
                  Сумма к оплате: <strong>{usdtWallet.amount} USDT</strong>
                </p>
                <p className="wallet-network">Сеть: TRC20</p>
                <p className="wallet-note">
                  ⚠️ Отправьте точную сумму на указанный адрес. После подтверждения транзакции звезды будут зачислены пользователю @{usdtWallet.username}.
                </p>
                <button className="back-btn" onClick={() => {
                  setPaymentMethod(null);
                  setUsdtWallet(null);
                }}>
                  Назад
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Создание платежа...</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default App;

